import React, { createContext, useContext, useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { signIn, signOut, signUp, confirmSignUp, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { useConfig } from './ConfigContext';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { config, isConfigured } = useConfig();

  // Configure Amplify when config is available
  useEffect(() => {
    if (isConfigured && config) {
      try {
        Amplify.configure({
          Auth: {
            Cognito: {
              userPoolId: config.cognitoUserPoolId,
              userPoolClientId: config.cognitoClientId,
              region: config.region,
            }
          }
        });
        
        // Check if user is already authenticated
        checkAuthState();
      } catch (error) {
        console.error('Error configuring Amplify:', error);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [config, isConfigured]);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      
      if (currentUser && session.tokens) {
        setUser(currentUser);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.log('No authenticated user found');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      const result = await signIn({
        username: email,
        password: password,
      });

      if (result.isSignedIn) {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { success: false, error: 'Sign in not completed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.message || 'Login failed' 
      };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email, password, name) => {
    try {
      setLoading(true);
      const result = await signUp({
        username: email,
        password: password,
        options: {
          userAttributes: {
            email: email,
            name: name,
          },
        },
      });

      return { 
        success: true, 
        needsConfirmation: !result.isSignUpComplete,
        message: 'Please check your email for the verification code' 
      };
    } catch (error) {
      console.error('Signup error:', error);
      return { 
        success: false, 
        error: error.message || 'Signup failed' 
      };
    } finally {
      setLoading(false);
    }
  };

  const confirmSignup = async (email, confirmationCode) => {
    try {
      setLoading(true);
      await confirmSignUp({
        username: email,
        confirmationCode: confirmationCode,
      });

      return { success: true };
    } catch (error) {
      console.error('Confirmation error:', error);
      return { 
        success: false, 
        error: error.message || 'Verification failed' 
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getAuthToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    signup,
    confirmSignup,
    logout,
    getAuthToken,
    checkAuthState,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
